import { useState } from "react";
import axios from "axios";
import { API } from "../App";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { User, MapPin, Loader2 } from "lucide-react";
import { toast } from "sonner";

export default function CompleteProfileModal({ onClose, onComplete }) {
  const [formData, setFormData] = useState({
    dateOfBirth: "",
    street: "",
    city: "",
    state: "",
    zipCode: "",
  });
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.dateOfBirth || !formData.street || !formData.city || !formData.state || !formData.zipCode) {
      toast.error("Please fill in all fields");
      return;
    }
    setLoading(true);
    try {
      await axios.put(`${API}/profile`, {
        date_of_birth: formData.dateOfBirth,
        address: {
          street: formData.street,
          city: formData.city,
          state: formData.state,
          zip_code: formData.zipCode,
          country: "United States",
        },
      });
      toast.success("Profile completed!");
      onComplete?.();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to update profile");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" data-testid="complete-profile-modal">
      <Card className="w-full max-w-md border-gold/30">
        <CardHeader className="text-center">
          <CardTitle className="font-serif text-xl">Complete Your Profile</CardTitle>
          <CardDescription>Help us personalize your experience.</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="dateOfBirth" className="flex items-center gap-2">
                <User className="h-3 w-3" /> Date of Birth
              </Label>
              <Input
                id="dateOfBirth"
                name="dateOfBirth"
                type="date"
                value={formData.dateOfBirth}
                onChange={handleChange}
                data-testid="profile-dob-input"
              />
            </div>

            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                <MapPin className="h-3 w-3" /> Address
              </Label>
              <Input
                name="street"
                placeholder="Street address"
                value={formData.street}
                onChange={handleChange}
                data-testid="profile-street-input"
              />
              <div className="grid grid-cols-3 gap-2">
                <Input
                  name="city"
                  placeholder="City"
                  value={formData.city}
                  onChange={handleChange}
                  data-testid="profile-city-input"
                />
                <Input
                  name="state"
                  placeholder="State"
                  value={formData.state}
                  onChange={handleChange}
                  data-testid="profile-state-input"
                />
                <Input
                  name="zipCode"
                  placeholder="ZIP"
                  value={formData.zipCode}
                  onChange={handleChange}
                  data-testid="profile-zip-input"
                />
              </div>
            </div>

            <Button
              type="submit"
              className="w-full bg-gold text-primary-foreground hover:bg-gold/90 font-serif"
              disabled={loading}
              data-testid="profile-submit-btn"
            >
              {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              Save Profile
            </Button>
            <button
              type="button"
              onClick={onClose}
              className="w-full text-center text-sm text-muted-foreground hover:text-foreground"
              data-testid="profile-skip-btn"
            >
              I'll do this later
            </button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
